// src/services/promotion.service.js
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { db } from "@/firebase";
import { FeatureFlags } from "@/config/features";
import { Logger } from "@/utils/logger";

/**
 * Promotion Service
 * Handles all promotional features and logic for the tutoring platform
 */
export class PromotionService {
  // Promotion types
  static PROMOTION_TYPES = {
    FIRST_LESSON_FREE: "first_lesson_free",
    FIRST_LESSON_DISCOUNT: "first_lesson_discount",
    PERCENTAGE_DISCOUNT: "percentage_discount",
    FIXED_AMOUNT_DISCOUNT: "fixed_amount_discount",
  };

  // Default first lesson promotion settings
  static DEFAULT_FIRST_LESSON_PROMOTION = {
    type: this.PROMOTION_TYPES.FIRST_LESSON_DISCOUNT,
    value: 50, // 50% discount by default
    enabled: true,
    maxUsesPerUser: 1,
    description: "50% off your first lesson with any teacher",
  };

  /**
   * Check if first lesson promotions are enabled globally
   */
  static isFirstLessonPromotionEnabled() {
    return FeatureFlags.payments.firstLessonPromotions;
  }

  /**
   * Check if a user is eligible for first lesson promotion
   * @param {string} userId - The user/student ID
   * @returns {Promise<Object>} - Eligibility status and details
   */
  static async checkFirstLessonEligibility(userId) {
    try {
      Logger.log(
        "info",
        "Checking first lesson eligibility",
        { userId },
        "PromotionService"
      );

      if (!this.isFirstLessonPromotionEnabled()) {
        return {
          eligible: false,
          reason: "first_lesson_promotions_disabled",
          promotion: null,
        };
      }

      // Check user's promotional usage in the userPromotions table
      const userPromoUsage = await this.getUserPromotionalUsage(userId);
      const hasUsedFirstLesson = userPromoUsage.firstLessonUsed || false;

      if (hasUsedFirstLesson) {
        return {
          eligible: false,
          reason: "already_used_first_lesson_promotion",
          promotion: null,
        };
      }

      // Enhanced check: also check lesson history to ensure no existing lessons
      const lessonHistory = await this.getUserLessonHistory(userId);

      if (lessonHistory.totalLessons > 0) {
        Logger.log(
          "info",
          "User has existing lesson history, not eligible for first lesson promotion",
          { userId, totalLessons: lessonHistory.totalLessons },
          "PromotionService"
        );

        return {
          eligible: false,
          reason: "existing_lesson_history",
          promotion: null,
          details: {
            totalLessons: lessonHistory.totalLessons,
            promotionalLessons: lessonHistory.promotionalLessons,
          },
        };
      }

      // Return default first lesson promotion
      const promotion = this.DEFAULT_FIRST_LESSON_PROMOTION;

      Logger.log(
        "info",
        "User is eligible for first lesson promotion",
        { userId, promotion },
        "PromotionService"
      );

      return {
        eligible: true,
        reason: "eligible_for_first_lesson",
        promotion,
      };
    } catch (error) {
      Logger.log(
        "error",
        "Error checking first lesson eligibility",
        { error: error.message, userId },
        "PromotionService"
      );
      throw error;
    }
  }

  /**
   * Get user's promotional usage history
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} - User's promotional usage data
   */
  static async getUserPromotionalUsage(userId) {
    try {
      const userPromoRef = doc(db, "userPromotions", userId);
      const userPromoDoc = await getDoc(userPromoRef);

      if (!userPromoDoc.exists()) {
        // Return default structure for new users
        return {
          firstLessonUsed: false,
          totalPromotionsUsed: 0,
          promotionHistory: [],
          createdAt: null,
          updatedAt: null,
        };
      }

      return userPromoDoc.data();
    } catch (error) {
      Logger.log(
        "error",
        "Error getting user promotional usage",
        { error: error.message, userId },
        "PromotionService"
      );
      throw error;
    }
  }

  /**
   * Get user's lesson history to check for existing lessons
   * @param {string} userId - The user/student ID
   * @returns {Promise<Object>} - User's lesson history summary
   */
  static async getUserLessonHistory(userId) {
    try {
      Logger.log(
        "info",
        "Checking user lesson history",
        { userId },
        "PromotionService"
      );

      // Query all lessons for this student
      const lessonsQuery = query(
        collection(db, "lessons"),
        where("studentId", "==", userId)
      );

      const lessonsSnapshot = await getDocs(lessonsQuery);
      const lessons = [];

      lessonsSnapshot.forEach((doc) => {
        lessons.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      // Filter out cancelled lessons for promotional eligibility
      const completedLessons = lessons.filter(
        (lesson) => lesson.status !== "cancelled"
      );

      // Count promotional vs regular lessons (only completed ones)
      let promotionalLessons = 0;
      let regularLessons = 0;

      completedLessons.forEach((lesson) => {
        if (lesson.isPromotional || lesson.promotional === true) {
          promotionalLessons++;
        } else {
          regularLessons++;
        }
      });

      const lessonHistory = {
        totalLessons: completedLessons.length, // Only count completed lessons for eligibility
        totalAllLessons: lessons.length, // Keep track of all lessons including cancelled
        cancelledLessons: lessons.length - completedLessons.length,
        promotionalLessons,
        regularLessons,
        lessons: lessons, // Include full lesson data for debugging
      };

      Logger.log(
        "info",
        "User lesson history retrieved",
        {
          userId,
          completedLessons: lessonHistory.totalLessons,
          cancelledLessons: lessonHistory.cancelledLessons,
          totalAllLessons: lessonHistory.totalAllLessons,
          promotionalLessons: lessonHistory.promotionalLessons,
          regularLessons: lessonHistory.regularLessons,
        },
        "PromotionService"
      );

      return lessonHistory;
    } catch (error) {
      Logger.log(
        "error",
        "Error getting user lesson history",
        { error: error.message, userId },
        "PromotionService"
      );
      throw error;
    }
  }

  /**
   * Calculate promotional discount for a lesson
   * @param {number} originalAmount - Original lesson price in cents
   * @param {Object} promotion - Promotion details
   * @returns {Object} - Discount calculation details
   */
  static calculatePromotionalDiscount(originalAmount, promotion) {
    try {
      if (!promotion || !promotion.enabled) {
        return {
          originalAmount,
          discountAmount: 0,
          finalAmount: originalAmount,
          discountPercentage: 0,
          promotionApplied: false,
        };
      }

      let discountAmount = 0;

      switch (promotion.type) {
        case this.PROMOTION_TYPES.FIRST_LESSON_FREE:
          discountAmount = originalAmount;
          break;

        case this.PROMOTION_TYPES.FIRST_LESSON_DISCOUNT:
        case this.PROMOTION_TYPES.PERCENTAGE_DISCOUNT:
          discountAmount = Math.round((originalAmount * promotion.value) / 100);
          break;

        case this.PROMOTION_TYPES.FIXED_AMOUNT_DISCOUNT:
          discountAmount = Math.min(promotion.value, originalAmount);
          break;

        default:
          Logger.log(
            "warn",
            "Unknown promotion type",
            { promotionType: promotion.type },
            "PromotionService"
          );
          break;
      }

      const finalAmount = Math.max(0, originalAmount - discountAmount);
      const discountPercentage =
        originalAmount > 0 ? (discountAmount / originalAmount) * 100 : 0;

      return {
        originalAmount,
        discountAmount,
        finalAmount,
        discountPercentage: Math.round(discountPercentage),
        promotionApplied: discountAmount > 0,
        promotion: promotion,
      };
    } catch (error) {
      Logger.log(
        "error",
        "Error calculating promotional discount",
        { error: error.message, originalAmount, promotion },
        "PromotionService"
      );
      throw error;
    }
  }

  /**
   * Apply first lesson promotion for a user
   * @param {string} userId - The user ID
   * @param {string} lessonId - The lesson ID
   * @param {Object} promotionDetails - Applied promotion details
   * @returns {Promise<string>} - Promotion record ID
   */
  static async applyFirstLessonPromotion(userId, lessonId, promotionDetails) {
    try {
      Logger.log(
        "info",
        "Applying first lesson promotion",
        { userId, lessonId, promotionDetails },
        "PromotionService"
      );

      // Create promotion usage record
      const promotionUsageRef = collection(db, "promotionUsage");
      const usageRecord = {
        userId,
        lessonId,
        promotionType: this.PROMOTION_TYPES.FIRST_LESSON_DISCOUNT,
        originalAmount: promotionDetails.originalAmount,
        discountAmount: promotionDetails.discountAmount,
        finalAmount: promotionDetails.finalAmount,
        appliedAt: serverTimestamp(),
        status: "applied",
        metadata: {
          promotion: promotionDetails.promotion,
        },
      };

      const usageDocRef = await addDoc(promotionUsageRef, usageRecord);

      // Update user's promotional usage summary
      await this.updateUserPromotionalUsage(userId, {
        firstLessonUsed: true,
        totalPromotionsUsed: increment(1),
        lastPromotionUsedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      Logger.log(
        "info",
        "First lesson promotion applied successfully",
        { userId, lessonId, usageId: usageDocRef.id },
        "PromotionService"
      );

      return usageDocRef.id;
    } catch (error) {
      Logger.log(
        "error",
        "Error applying first lesson promotion",
        { error: error.message, userId, lessonId },
        "PromotionService"
      );
      throw error;
    }
  }

  /**
   * Update user's promotional usage summary
   * @param {string} userId - The user ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<void>}
   */
  static async updateUserPromotionalUsage(userId, updateData) {
    try {
      const userPromoRef = doc(db, "userPromotions", userId);
      const userPromoDoc = await getDoc(userPromoRef);

      if (!userPromoDoc.exists()) {
        // Create new record if it doesn't exist
        await updateDoc(userPromoRef, {
          firstLessonUsed: false,
          totalPromotionsUsed: 0,
          promotionHistory: [],
          createdAt: serverTimestamp(),
          ...updateData,
        });
      } else {
        // Update existing record
        await updateDoc(userPromoRef, updateData);
      }
    } catch (error) {
      Logger.log(
        "error",
        "Error updating user promotional usage",
        { error: error.message, userId },
        "PromotionService"
      );
      throw error;
    }
  }

  /**
   * Get teacher's first lesson promotion settings
   * @param {string} teacherId - The teacher ID
   * @returns {Promise<Object>} - Teacher's first lesson settings
   */
  static async getTeacherFirstLessonSettings(teacherId) {
    try {
      const teacherDoc = await getDoc(doc(db, "teachers", teacherId));

      if (!teacherDoc.exists()) {
        throw new Error("Teacher not found");
      }

      const teacherData = teacherDoc.data();
      const firstLessonSettings = teacherData.firstLessonSettings || {
        enabled: true,
        customDiscount: null, // null means use default platform promotion
        customDescription: null,
      };

      return firstLessonSettings;
    } catch (error) {
      Logger.log(
        "error",
        "Error getting teacher first lesson settings",
        { error: error.message, teacherId },
        "PromotionService"
      );
      throw error;
    }
  }

  /**
   * Update teacher's first lesson promotion settings
   * @param {string} teacherId - The teacher ID
   * @param {Object} settings - New settings
   * @returns {Promise<void>}
   */
  static async updateTeacherFirstLessonSettings(teacherId, settings) {
    try {
      const teacherRef = doc(db, "teachers", teacherId);
      await updateDoc(teacherRef, {
        firstLessonSettings: settings,
        updatedAt: serverTimestamp(),
      });

      Logger.log(
        "info",
        "Teacher first lesson settings updated",
        { teacherId, settings },
        "PromotionService"
      );
    } catch (error) {
      Logger.log(
        "error",
        "Error updating teacher first lesson settings",
        { error: error.message, teacherId },
        "PromotionService"
      );
      throw error;
    }
  }

  /**
   * Get promotion usage statistics
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} - Usage statistics
   */
  static async getPromotionUsageStats(filters = {}) {
    try {
      let q = collection(db, "promotionUsage");

      // Apply filters if provided
      if (filters.promotionType) {
        q = query(q, where("promotionType", "==", filters.promotionType));
      }

      if (filters.userId) {
        q = query(q, where("userId", "==", filters.userId));
      }

      if (filters.teacherId) {
        q = query(q, where("teacherId", "==", filters.teacherId));
      }

      const querySnapshot = await getDocs(q);
      const usageRecords = [];

      querySnapshot.forEach((doc) => {
        usageRecords.push({
          id: doc.id,
          ...doc.data(),
          appliedAt: doc.data().appliedAt?.toDate(),
        });
      });

      // Calculate statistics
      const stats = {
        totalUsage: usageRecords.length,
        totalDiscountAmount: usageRecords.reduce(
          (sum, record) => sum + (record.discountAmount || 0),
          0
        ),
        averageDiscount: 0,
        firstLessonPromotions: usageRecords.filter(
          (r) => r.promotionType === this.PROMOTION_TYPES.FIRST_LESSON_DISCOUNT
        ).length,
        uniqueUsers: new Set(usageRecords.map((r) => r.userId)).size,
      };

      if (stats.totalUsage > 0) {
        stats.averageDiscount = Math.round(
          stats.totalDiscountAmount / stats.totalUsage
        );
      }

      return {
        stats,
        records: usageRecords,
      };
    } catch (error) {
      Logger.log(
        "error",
        "Error getting promotion usage stats",
        { error: error.message, filters },
        "PromotionService"
      );

      // If it's a permission error and we're querying for a specific teacher,
      // return empty stats instead of throwing - this handles the case where
      // the collection doesn't exist or has no documents for this teacher
      if (error.code === "permission-denied" && filters.teacherId) {
        Logger.log(
          "info",
          "Returning empty promotion stats due to permission error",
          { teacherId: filters.teacherId },
          "PromotionService"
        );
        return {
          stats: {
            totalUsage: 0,
            totalDiscountAmount: 0,
            averageDiscountAmount: 0,
            promotionTypes: [],
          },
          records: [],
        };
      }

      throw error;
    }
  }

  /**
   * Format promotional discount for display
   * @param {Object} discountInfo - Discount calculation details
   * @returns {Object} - Formatted display information
   */
  static formatPromotionalDiscount(discountInfo) {
    if (!discountInfo.promotionApplied) {
      return {
        showPromotion: false,
        originalPrice: this.formatCurrency(discountInfo.originalAmount),
        finalPrice: this.formatCurrency(discountInfo.finalAmount),
      };
    }

    return {
      showPromotion: true,
      originalPrice: this.formatCurrency(discountInfo.originalAmount),
      finalPrice: this.formatCurrency(discountInfo.finalAmount),
      discountAmount: this.formatCurrency(discountInfo.discountAmount),
      discountPercentage: discountInfo.discountPercentage,
      savings: this.formatCurrency(discountInfo.discountAmount),
      promotionDescription:
        discountInfo.promotion?.description || "Promotional discount applied",
      isFree: discountInfo.finalAmount === 0,
    };
  }

  /**
   * Format currency for display (helper method)
   * @param {number} pence - Amount in pence
   * @returns {string} - Formatted currency string
   */
  static formatCurrency(pence) {
    const pounds = pence / 100;
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(pounds);
  }

  /**
   * Validate promotion configuration
   * @param {Object} promotion - Promotion configuration
   * @returns {Object} - Validation result
   */
  static validatePromotion(promotion) {
    const errors = [];

    if (!promotion) {
      errors.push("Promotion object is required");
      return { valid: false, errors };
    }

    if (
      !promotion.type ||
      !Object.values(this.PROMOTION_TYPES).includes(promotion.type)
    ) {
      errors.push("Invalid promotion type");
    }

    if (typeof promotion.enabled !== "boolean") {
      errors.push("Enabled flag must be a boolean");
    }

    if (
      [
        this.PROMOTION_TYPES.PERCENTAGE_DISCOUNT,
        this.PROMOTION_TYPES.FIRST_LESSON_DISCOUNT,
      ].includes(promotion.type)
    ) {
      if (
        typeof promotion.value !== "number" ||
        promotion.value < 0 ||
        promotion.value > 100
      ) {
        errors.push("Percentage value must be between 0 and 100");
      }
    }

    if (promotion.type === this.PROMOTION_TYPES.FIXED_AMOUNT_DISCOUNT) {
      if (typeof promotion.value !== "number" || promotion.value < 0) {
        errors.push("Fixed amount value must be a positive number");
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Check if a student is eligible for first lesson promotion
   * @param {string} studentId - The student ID
   * @returns {Promise<boolean>} - Eligibility status
   */
  static async isStudentEligibleForFirstLesson(studentId) {
    try {
      const eligibility = await this.checkFirstLessonEligibility(studentId);
      return eligibility.eligible;
    } catch (error) {
      Logger.log(
        "error",
        "Error checking student first lesson eligibility",
        {
          studentId,
          error: error.message,
        },
        "PromotionService"
      );
      return false;
    }
  }

  /**
   * Get global promotion settings
   * @returns {Promise<Object>} - Global promotion settings
   */
  static async getGlobalPromotionSettings() {
    try {
      return {
        enabled: this.isFirstLessonPromotionEnabled(),
        defaultDiscount: this.DEFAULT_FIRST_LESSON_PROMOTION,
      };
    } catch (error) {
      Logger.log(
        "error",
        "Error getting global promotion settings",
        {
          error: error.message,
        },
        "PromotionService"
      );
      return {
        enabled: false,
        defaultDiscount: null,
      };
    }
  }
}

export default PromotionService;
